import React, { useState, useEffect } from 'react';
import './LandingPage.css';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { FaInstagram, FaTiktok, FaYoutube, FaTwitter, FaFacebookF, FaApple, FaGooglePlay } from 'react-icons/fa';
import fervoirLogo from '../img/Fervoir-Logo.png';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const [headerOpacity, setHeaderOpacity] = useState(1);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const fadeDistance = 400;
      const newOpacity = Math.max(0, 1 - scrollPosition / fadeDistance);
      setScrollOpacity(newOpacity);

      const footerElement = document.querySelector('.landing-page-footer');
      if (footerElement) {
        const footerRect = footerElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (footerRect.top < windowHeight) {
          const footerVisible = windowHeight - footerRect.top;
          const footerHeight = footerRect.height;
          const fadeProgress = Math.min(footerVisible / (footerHeight * 0.5), 1);
          const newHeaderOpacity = Math.max(0, 1 - fadeProgress);
          setHeaderOpacity(newHeaderOpacity);
        } else {
          setHeaderOpacity(1);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCreateAccount = () => {
    navigate('/registration');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="landing-page">
      <header
        className="landing-page-header header-visible"
        style={{ opacity: headerOpacity }}
      >
        <div className="header-content">
          <div className="logo-section">
            <h1 className="tinder-logo">
              <img src={fervoirLogo} alt="Fervoir logo" className="fervoir-logo-img" />
              Fervoir
            </h1>
          </div>

          <nav className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a href="#products" className="nav-link1">Products</a>
            <a href="#learn" className="nav-link1">Learn</a>
            <a href="#safety" className="nav-link1">Safety</a>
            <a href="#support" className="nav-link1">Support</a>
            <a href="#download" className="nav-link1">Download</a>
          </nav>

          <div className="header-actions">
            <button className="login-header-btn" onClick={handleLogin}>
              Log in
            </button>
            <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <main
        className="landing-hero-section"
        style={{
          opacity: scrollOpacity,
          transform: `translateY(${(1 - scrollOpacity) * -30}px)`,
        }}
      >
        <div className="landing-hero-content">
          <h1 className="landing-hero-title">Turn sparks into stories.</h1>
          <button className="landing-cta-button" onClick={handleCreateAccount}>
            Create account
          </button>
        </div>
      </main>

      <footer className="landing-page-footer">
        <div className="landing-footer-content">
          <div className="landing-footer-sections">
            <div className="landing-footer-section">
              <h3>Legal</h3>
              <ul>
                <li><a href="/all-policies" onClick={(e) => { e.preventDefault(); navigate('/all-policies'); }}>All Policies</a></li>
                <li><a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>Privacy</a></li>
                <li><a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>Terms</a></li>
                <li><a href="/consent" onClick={(e) => { e.preventDefault(); navigate('/consent'); }}>Consumer Health Data Privacy Policy</a></li>
                <li><a href="/cookies" onClick={(e) => { e.preventDefault(); navigate('/cookies'); }}>Cookie Policy</a></li>
                <li><a href="#" onClick={(e) => e.preventDefault()}>Intellectual Property</a></li>
                <li><a href="#" onClick={(e) => e.preventDefault()}>Accessibility Statement</a></li>
              </ul>
            </div>

            <div className="landing-footer-section">
              <h3>Careers</h3>
              <ul>
                <li><a href="#" onClick={(e) => e.preventDefault()}>Careers Portal</a></li>
                <li><a href="/blog" onClick={(e) => { e.preventDefault(); navigate('/blog'); }}>Tech Blog</a></li>
              </ul>
            </div>

            <div className="landing-footer-section">
              <h3>Social</h3>
              <div className="social-icons">
                <a href="#" onClick={(e) => e.preventDefault()} aria-label="Instagram"><FaInstagram /></a>
                <a href="#" onClick={(e) => e.preventDefault()} aria-label="TikTok"><FaTiktok /></a>
                <a href="#" onClick={(e) => e.preventDefault()} aria-label="YouTube"><FaYoutube /></a>
                <a href="#" onClick={(e) => e.preventDefault()} aria-label="Twitter"><FaTwitter /></a>
                <a href="#" onClick={(e) => e.preventDefault()} aria-label="Facebook"><FaFacebookF /></a>
              </div>
            </div>

            <div className="landing-footer-section">
              <h3>FAQ</h3>
              <ul>
                <li><a href="#" onClick={(e) => e.preventDefault()}>Destinations</a></li>
                <li><a href="#" onClick={(e) => e.preventDefault()}>Press Room</a></li>
                <li><a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>Contact</a></li>
                <li><a href="#" onClick={(e) => e.preventDefault()}>Promo Code</a></li>
              </ul>
            </div>
          </div>

          <div className="landing-footer-app-download">
            <h2>Get the app!</h2>
            <div className="download-buttons">
              <a href="#" className="download-btn apple-btn" onClick={(e) => e.preventDefault()}>
                <FaApple className="download-icon" />
                <div className="download-text">
                  <span className="download-label">Download on the</span>
                  <span className="download-store">App Store</span>
                </div>
              </a>
              <a href="#" className="download-btn google-btn" onClick={(e) => e.preventDefault()}>
                <FaGooglePlay className="download-icon" />
                <div className="download-text">
                  <span className="download-label">GET IT ON</span>
                  <span className="download-store">Google Play</span>
                </div>
              </a>
            </div>
          </div>

          <div className="landing-footer-description">
            <p>
              Welcome to Fervoir — where meaningful connections begin.
              Whether you’re searching for love, companionship, or a refreshing new connection, Fervoir gives you access to a global community of singles ready to meet someone like you. In a world where most relationships start online, Fervoir keeps things simple, safe, and authentic.
            </p>
            <p>
              Every identity and orientation is embraced here — love has no labels on Fervoir.
              Find a long-term partner, discover new friends, or immerse yourself in new campus experiences with Fervoir.
              This is not just a dating app. It’s a space built for real people and real possibilities.
            </p>
          </div>

          <div className="landing-footer-bottom">
            <div className="landing-footer-links-wrapper">
              <a href="/faq" onClick={(e) => { e.preventDefault(); navigate('/faq'); }}>FAQ</a>
              <span className="landing-separator">/</span>
              <a href="/safety-tips" onClick={(e) => { e.preventDefault(); navigate('/safety-tips'); }}>Safety Tips</a>
              <span className="landing-separator">/</span>
              <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>Terms</a>
              <span className="landing-separator">/</span>
              <a href="/cookies" onClick={(e) => { e.preventDefault(); navigate('/cookies'); }}>Cookie Policy</a>
              <span className="landing-separator">/</span>
              <a href="/privacy-settings" onClick={(e) => { e.preventDefault(); navigate('/privacy-settings'); }}>Privacy Settings</a>
            </div>
            <p className="landing-footer-copyright">© 2025 Fervoir LLC, All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
